// src/app/auth/services/auth.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoadingService } from '../../common/loading.service';
import { Router } from '@angular/router';
import { ApiEndpointService } from '../../common/api-endpoint.service';

export interface CurrentUser {
  userId: string;
  email: string;
  roles: string[];

  firstName: string;
  lastName: string;
  displayName: string;

  // HealthTrio session-scoped context
  providerId?: string;
  providerNpi?: string;
  providerFirstName?: string;
  providerLastName?: string;
  facilityName?: string;

  authSource?: string;
  apiBaseHint?: string;

  isInternal?: boolean;

}


/** Legacy shape used around the app when it decoded the JWT. Keep it exported so imports compile. */
export interface MyJwtPayload {
  userEmail?: string;
  userId?: string;
  role?: string | string[];
  exp?: number;

  // legacy optional fields – leave as undefined
  providerID?: string;
  providerNPI?: string;
  userFirstName?: string;
  userLastName?: string;
  providerFirstName?: string;
  providerLastName?: string;
  facilityName?: string;

  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private currentUser?: CurrentUser;
  private inflight?: Promise<CurrentUser | null>;

  constructor(
    private http: HttpClient,
    private spinner: LoadingService,
    private router: Router,
    private apiEndpoint: ApiEndpointService
  ) { }
  private authBase(): string {
    // Dev: SPA on 4200, API on 7013
    if (location.hostname === 'localhost' && location.port === '4200') {
      return 'https://localhost:7013';
    }
    // Hosted envs: hit current origin (cookie-bound host)
    return '';
  }

  private url(path: string): string {
    const base = this.authBase();
    return base ? `${base}${path}` : path; // absolute in dev, relative elsewhere
  }

  async getCurrentUser(forceRefresh = false): Promise<CurrentUser | null> {
    if (this.currentUser && !forceRefresh) return this.currentUser;
    if (this.inflight && !forceRefresh) return this.inflight;

    this.inflight = (async () => {
      try {
        this.spinner.show();
        const mePath = this.url('/auth/me');
        console.log('[AuthService] - mePath: ', mePath);
        const me = await firstValueFrom(
          this.http.get<CurrentUser>(mePath)
        );
        this.currentUser = me;
        this.apiEndpoint.setApiBase(me.apiBaseHint || '');
        return me;
      } catch {
        this.spinner.hide();
        this.currentUser = undefined;
        return null;
      } finally {
        this.spinner.hide();
        this.inflight = undefined;
      }
    })();

    return this.inflight;
  }

  /** Force re-pull (e.g., after role changes). */
  async refresh(): Promise<CurrentUser | null> {
    return this.getCurrentUser(true);
  }

  /** ------ Simple getters used around the app ------ */
  getUser(): CurrentUser | undefined { return this.currentUser; }
  getUserRoles(): string[] { return this.currentUser?.roles ?? []; }
  getUserEmail(): string | null { return this.currentUser?.email ?? null; }
  getUserId(): string | null { return this.currentUser?.userId ?? null; }

  isAuthenticated(): boolean {
    return !!this.currentUser;
  }

  /** For nav header */
  getDisplayName(): string {
    const u = this.currentUser;
    if (!u) return '';
    return u.displayName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
  }

  /** HealthTrio session-scoped provider context (undefined for AzureAD users) */
  getProviderContext(): {
    providerId?: string;
    providerNpi?: string;
    providerFirstName?: string;
    providerLastName?: string;
    facilityName?: string;
  } | undefined {
    if (!this.currentUser) return undefined;
    const {
      providerId, providerNpi, providerFirstName, providerLastName, facilityName
    } = this.currentUser;
    // If none present, return undefined so callers can branch cleanly
    const any =
      providerId || providerNpi || providerFirstName || providerLastName || facilityName;
    return any ? { providerId, providerNpi, providerFirstName, providerLastName, facilityName } : undefined;
  }

  clearCache(): void {
    this.currentUser = undefined;
    sessionStorage.removeItem('runtimeAppSettings:timeout');
  }

  /** Server logout + local cache clear + optional navigation */
  async logout(redirectTo: string | null = '/login'): Promise<void> {
    //const logoutPath = `${environment.frontDoorAPIUri}/Auth/logout`;
    const logoutPath = this.url('/auth/logout'); 
    try {
      await firstValueFrom(this.http.post(logoutPath, {}));
    } finally {
      this.clearCache();
      if (redirectTo) {
        this.router.navigateByUrl(redirectTo);
      }
    }
  }


  // ------- Compatibility shims -------
  setToken(_: string): void { /* noop */ }
  getToken(): string | null { return null; }
  clearToken(): void { /* noop */ }
  getDecodedToken(): MyJwtPayload | null {
    if (!this.currentUser) return null;
    return { userEmail: this.currentUser.email, userId: this.currentUser.userId, role: this.currentUser.roles };
  }
  getCanForceUnlock(): boolean {
    const roles = this.getUserRoles();
    return roles.includes('Admin') || roles.includes('SuperAdmin');
  }
  isTokenExpired(): boolean { return !this.currentUser; }
}

